import React, { useState } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import type { DrillTarget } from '../../../lib/campaign-analytics/drill';
import type { AnalyticsQuestion } from '../../../lib/campaign-analytics/normalize';
import type { QuestionSummary } from '../../../lib/campaign-analytics/summarize';
import { QuestionCard } from './QuestionCard';
import { toneAt } from './chartTheme';

/** Props for {@link HighlightsCarousel}. */
export interface HighlightsCarouselProps {
  /** Omit while the answer-dependent highlight selection is loading. */
  items?: QuestionSummary[];
  /** Questions by field id, for option thumbnails. */
  questions?: Map<string, AnalyticsQuestion>;
  onDrill?: (target: DrillTarget, label: string) => void;
  onAsk?: (prompt: string, summary: QuestionSummary) => void;
}

/** Cards per page. */
const PAGE_SIZE = 2;

/**
 * The clearest results first: question cards two at a time, each in its own
 * tone, paged with dots and arrows like the report's Key Quotes.
 */
export const HighlightsCarousel: React.FC<HighlightsCarouselProps> = ({ items, questions, onDrill, onAsk }) => {
  const [page, setPage] = useState(0);
  if (items && !items.length) return null;
  const pages = items ? Math.ceil(items.length / PAGE_SIZE) : 1;
  const current = Math.min(page, pages - 1);
  const visible = items ? items.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE) : Array<undefined>(PAGE_SIZE).fill(undefined);
  const arrow = 'rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring';
  return (
    <section aria-label="Highlights" className="relative flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">Highlights</h2>
        {pages > 1 && (
          <div className="flex items-center gap-3">
            <span aria-hidden="true" className="flex gap-1.5">
              {Array.from({ length: pages }, (_, index) => (
                <span key={index} className={`h-1.5 w-1.5 rounded-full ${index === current ? 'bg-accent-soft' : 'bg-border'}`} />
              ))}
            </span>
            <span className="sr-only">{`${current + 1} / ${pages}`}</span>
            <button type="button" aria-label="Previous highlights" className={arrow} onClick={() => setPage((current - 1 + pages) % pages)}>
              <IconChevronLeft aria-hidden="true" className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Next highlights" className={arrow} onClick={() => setPage((current + 1) % pages)}>
              <IconChevronRight aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        )}
      </header>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {visible.map((summary, index) => (
          <QuestionCard key={index} summary={summary} question={summary && questions?.get(summary.fieldId)} onDrill={onDrill} onAsk={onAsk} tone={toneAt(current * PAGE_SIZE + index)} />
        ))}
      </div>
    </section>
  );
};
